import React, { useMemo } from "react";
import { isEmpty } from "lodash";
import { getNetTotalEnergyConsumption } from "utils/results";
import { buildInfo, isOverlapping } from "utils/nzPDFReport";

import HeaderImage from "assets/images/nzCaseStudy/header.png";
import GraphImage from "assets/images/nzCaseStudy/chevron2.png";
import FooterImage from "assets/images/nzCaseStudy/footerNZ.png";

import "../styles.css";

export const sectionColorLabel = {
    spaceHeating: {
        color: "#80BA3C",
        label: "Space Heating",
    },
    lightAndAppliances: {
        color: "#053747",
        label: "Light and appliances",
    },
    otherElectrical: {
        color: "#2F4965",
        label: "Other Electrical",
    },
    ventilation: {
        color: "#61C2A2",
        label: "Ventilation",
    },
    waterHeating: {
        color: "#089C60",
        label: "Water Heating",
    },
    spaceCooling: {
        color: "#017873",
        label: "Space Cooling",
    },
};

const ArrowWithoutInfo = ({ color, top = 0 }) => {
    return (
        <div
            style={{
                position: "absolute",
                right: "55px",
                top: `calc(${top}% - ${top < 9 ? "-8px" : "30px"})`,
                width: "fit-content",
            }}
        >
            <div
                style={{
                    alignSelf: "flex-start",
                    display: "flex",
                    justifyContent: "center",
                    height: "fit-content",
                }}
            >
                <svg width="25" height="25" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <polygon points="100,50 0,100 0,0" fill={color} stroke="#636365" strokeWidth={2} />
                </svg>
            </div>
        </div>
    );
};

const fixOverlappingArrowLabels = (infoToRender) => {
    const sortedInfoToRender = infoToRender.sort((infoA, infoB) => infoA.top - infoB.top);

    const alignedInfoToRender = sortedInfoToRender.reduce((acc, cur, index) => {
        const prev = index > 0 ? acc[index - 1] : null;

        return [
            ...acc,
            {
                ...cur,
                top:
                    !!prev && (isOverlapping(cur.top, prev.top) || cur.top < prev.top)
                        ? prev.top + 10
                        : cur.top < 9
                        ? cur.top + 10
                        : cur.top,
            },
        ];
    }, []);

    return alignedInfoToRender;
};

const ArrowLabels = ({ infoToRender }) => {
    const alignedInfoToRender = fixOverlappingArrowLabels(infoToRender);

    return (
        <div style={{ position: "absolute", right: "60px", height: "100%", zIndex: "99" }}>
            {alignedInfoToRender.map(({ label, value, color, top, isOverlapping }, index) => (
                <div
                    key={`arrow-label-${index}`}
                    style={{
                        position: "absolute",
                        right: 0,
                        top: `calc(${top}% - ${top < 9 ? "10px" : "50px"})`,
                        width: "165px",
                    }}
                >
                    <span>{label}</span>
                    <div
                        style={{
                            display: "flex",
                            height: "30px",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                gap: "2px",
                                alignItems: "center",
                                flex: 1,
                            }}
                        >
                            <span style={{ fontSize: "26px", fontWeight: "800", lineHeight: "20px" }}>{value}</span>
                            <span
                                style={{
                                    fontSize: "12px",
                                }}
                            >
                                GJ/
                                <br />
                                year
                            </span>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export const PieChartSVG = ({ rawData = {}, width = 250, height = 250 }) => {
    const total = Object.values(rawData).reduce((sum, value) => sum + value, 0);

    const data = Object.entries(rawData).map(([name, value]) => ({
        name,
        value,
        percentage: (value / total) * 100,
    }));

    const radius = 100;
    const center = 120;

    const toRadians = (degree) => (degree * Math.PI) / 180;

    const describeArc = (x, y, radius, startAngle, endAngle) => {
        const start = {
            x: x + radius * Math.cos(toRadians(startAngle)),
            y: y + radius * Math.sin(toRadians(startAngle)),
        };
        const end = {
            x: x + radius * Math.cos(toRadians(endAngle)),
            y: y + radius * Math.sin(toRadians(endAngle)),
        };
        const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

        return [
            `M ${x} ${y}`,
            `L ${start.x} ${start.y}`,
            `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
            "Z",
        ].join(" ");
    };

    let angleStart = 0;

    return (
        <svg width={width} height={height} viewBox="0 0 240 240">
            {data.map((slice, index) => {
                const angle = (slice.percentage / 100) * 360;
                const angleEnd = angleStart + angle;

                const path = describeArc(center, center, radius, angleStart, angleEnd);
                angleStart = angleEnd;

                return <path key={slice.name} d={path} fill={sectionColorLabel[slice.name].color} />;
            })}
        </svg>
    );
};

const NZReportPDFPage3 = ({
    preRetrofitModelData = {},
    postRetrofitModelData = {},
    preRetrofitResultsData = {},
    postRetrofitResultsData = {},
    energyReduction = 0,
    operatingCostSaving = 0,
    estimatedUpfront = 0,
    carbonPayback = 0,
    eFileBaseModelData = {}, //When this is provided (not empty), we need to get labels for the post-retrofit upgrade column from the e-file base model data
}) => {
    const postRetrofitUpgradeCaseKey = isEmpty(eFileBaseModelData) ? "socAllUpgrades" : "socBaseCase";
    const { annualEnergyConsumption: aecBeforeRetrofit = 0 } = useMemo(
        () => getNetTotalEnergyConsumption(preRetrofitResultsData["socBaseCase"] || {}),

        [preRetrofitResultsData]
    );
    const { annualEnergyConsumption: aecPostRetrofit = 0, pvProduction = 0 } = useMemo(
        () => getNetTotalEnergyConsumption(postRetrofitResultsData[postRetrofitUpgradeCaseKey] || {}),
        [postRetrofitResultsData]
    );

    const beforeRenovationData = useMemo(() => {
        const {
            Annual: {
                Consumption: {
                    SpaceHeating: { _attributes: { total: totalSpaceHeating = 0 } = {} } = {},
                    HotWater: { _attributes: { primary: primaryDHW = 0 } = {} } = {},
                    Electrical: {
                        _attributes: {
                            baseload = 0,
                            ventilation = 0,
                            spaceCooling = 0,
                            airConditioning = 0,
                            appliance = 0,
                            heatPump = 0,
                            lighting = 0,
                            // total: totalConsumption = 0,
                        } = {},
                    } = {},
                } = {},
            } = {},
        } = preRetrofitResultsData["socBaseCase"] || {};

        return {
            spaceHeating: totalSpaceHeating,
            spaceCooling,
            waterHeating: primaryDHW,
            ventilation: ventilation,
            lightAndAppliances: baseload,
            otherElectrical: airConditioning + appliance + heatPump + lighting,
            // totalConsumption,
        };
    }, [preRetrofitResultsData]);

    const afterRenovationData = useMemo(() => {
        const {
            Annual: {
                Consumption: {
                    SpaceHeating: { _attributes: { total: totalSpaceHeating = 0 } = {} } = {},
                    HotWater: { _attributes: { primary: primaryDHW = 0 } = {} } = {},
                    Electrical: {
                        _attributes: {
                            baseload = 0,
                            ventilation = 0,
                            spaceCooling = 0,
                            airConditioning = 0,
                            appliance = 0,
                            heatPump = 0,
                            lighting = 0,
                            // total: totalConsumption = 0,
                        } = {},
                    } = {},
                } = {},
            } = {},
        } = postRetrofitResultsData[postRetrofitUpgradeCaseKey] || {};

        return {
            spaceHeating: totalSpaceHeating,
            spaceCooling,
            waterHeating: primaryDHW,
            ventilation: ventilation,
            lightAndAppliances: baseload,
            otherElectrical: airConditioning + appliance + heatPump + lighting,
            // totalConsumption,
        };
    }, [postRetrofitResultsData]);

    // const { socBaseCase: { resultsSummary: { annualEmissions: preAnnualEmissions = 0 } = {} } = {} } =
    //     preRetrofitResultsData || {};
    // const { [postRetrofitUpgradeCaseKey]: { resultsSummary: { annualEmissions: postAnnualEmissions = 0 } = {} } = {} } =
    //     postRetrofitResultsData || {};

    const preRetrofitEmissions = preRetrofitModelData?.program?.results?.tsv?.ERSGHG || 0;

    const postRetrofitEmissions = postRetrofitUpgradeCaseKey.includes("BaseCase")
        ? postRetrofitModelData?.program?.results?.tsv?.ERSGHG || 0
        : postRetrofitModelData?.program?.results?.tsv?.UGRERSGHG || 0;

    const infoToRender = buildInfo({
        aecBeforeRetrofit,
        typicalNewHome: 100,
        aecPostRetrofit,
        pvProduction,
    });

    const displayCarbonInfo = !!estimatedUpfront && !!carbonPayback;
    const displayCostInfo = operatingCostSaving !== 0;

    return (
        <div className="bodyNZPDF">
            <img src={HeaderImage} alt="header" />
            <div
                className="pageContainerNZPDF"
                style={{ background: "#E6E7E9", height: displayCarbonInfo ? "" : "auto" }}
            >
                <div
                    style={{
                        display: "flex",
                        // height: "65%",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            width: "30%",
                            height: "100%",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                padding: "10px 0",
                                background: "#626261",
                            }}
                        >
                            <span
                                style={{
                                    fontSize: "26px",
                                    fontWeight: "800",
                                    color: "#fff",
                                    textAlign: "center",
                                }}
                            >
                                ANNUAL <br /> ENERGY USE
                            </span>
                        </div>
                        <div
                            style={{
                                background: "#fff",
                                marginRight: "5px",
                                padding: "10px 0px 0px 20px",
                                maxHeight: "480px",
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            <div
                                style={{
                                    position: "relative",
                                    height: "100%",
                                }}
                            >
                                {infoToRender.length > 0 && <ArrowLabels infoToRender={infoToRender} />}
                                {infoToRender.map(({ label, color, top }, index) => (
                                    <ArrowWithoutInfo key={label + index} color={color} top={top} />
                                ))}
                                <img src={GraphImage} style={{ height: "100%" }} alt="chevron" />
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            width: "70%",
                            background: "#fff",
                            height: "100%",
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    padding: "10px 0",
                                    background: "#93BB49",
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: "26px",
                                        fontWeight: "800",
                                        color: "#fff",
                                        textAlign: "center",
                                    }}
                                >
                                    ENERGY CONSUMPTION
                                    <br />
                                    BREAKDOWN
                                </span>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-around",
                                    marginTop: "20px",
                                }}
                            >
                                <div className="pieChartContainerNZPDF">
                                    <span className="pieChartTitleNZPDF">BEFORE RENOVATION</span>
                                    <PieChartSVG width={325} height={325} rawData={beforeRenovationData} />
                                </div>
                                <div
                                    className="pieChartContainerNZPDF"
                                    style={{
                                        gap: "8px",
                                    }}
                                >
                                    <span className="pieChartTitleNZPDF">AFTER RENOVATION</span>
                                    <PieChartSVG width={225} height={225} rawData={afterRenovationData} />
                                </div>
                            </div>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                width: "70%",
                                margin: "0 auto",
                                marginTop: "25px",
                            }}
                        >
                            {Object.entries(sectionColorLabel).map(([key, { color, label }]) => (
                                <div
                                    key={key}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        marginBottom: "5px",
                                        flex: "50%",
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "10px",
                                            height: "10px",
                                            background: color,
                                            marginRight: "10px",
                                            border: "1px solid #636365",
                                        }}
                                    />
                                    <span
                                        style={{
                                            fontSize: "14px",
                                        }}
                                    >
                                        {label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        zIndex: "20",
                    }}
                >
                    <span
                        style={{
                            fontSize: "26px",
                            fontWeight: "800",
                            color: "#626261",
                            textAlign: "center",
                            marginTop: "15px",
                        }}
                    >
                        AS BUILT ENERGY PERFORMANCE
                    </span>
                    <div
                        style={{
                            width: "90%",
                            border: "2px solid #C5C5C6",
                            margin: "0 auto",
                            marginTop: "15px",
                            marginBottom: "15px",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <div
                            style={{
                                borderBottom: "2px solid #C5C5C6",
                                width: "100%",
                                display: "flex",
                                flex: 1,
                            }}
                        >
                            <div
                                style={{
                                    width: "90%",
                                    paddingLeft: "10px",
                                    borderRight: "2px solid #C5C5C6",
                                    alignContent: "center",
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: "16px",
                                        fontWeight: "800",
                                        color: "#626261",
                                    }}
                                >
                                    ANNUAL ENERGY SAVINGS
                                </span>
                            </div>
                            <div
                                style={{
                                    width: "20%",
                                    margin: "10px",
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: "14px",
                                    }}
                                >
                                    {energyReduction}%
                                </span>
                            </div>
                        </div>
                        <div
                            style={{
                                borderBottom: displayCostInfo || displayCarbonInfo ? "2px solid #C5C5C6" : "",
                                width: "100%",
                                display: "flex",
                                flex: 1,
                            }}
                        >
                            <div
                                style={{
                                    width: "90%",
                                    paddingLeft: "10px",
                                    borderRight: "2px solid #C5C5C6",
                                    alignContent: "center",
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: "16px",
                                        fontWeight: "800",
                                        color: "#626261",
                                    }}
                                >
                                    GREENHOUSE GAS SAVINGS
                                </span>
                            </div>
                            <div
                                style={{
                                    width: "20%",
                                    margin: "10px",
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: "14px",
                                    }}
                                >
                                    {(preRetrofitEmissions - postRetrofitEmissions)?.toFixed(2) || 0.0} Tonnes
                                </span>
                            </div>
                        </div>
                        {displayCostInfo && (
                            <div
                                style={{
                                    borderBottom: displayCarbonInfo ? "2px solid #C5C5C6" : "",
                                    width: "100%",
                                    display: "flex",
                                    flex: 1,
                                }}
                            >
                                <div
                                    style={{
                                        width: "90%",
                                        paddingLeft: "10px",
                                        borderRight: "2px solid #C5C5C6",
                                        alignContent: "center",
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: "16px",
                                            fontWeight: "800",
                                            color: "#626261",
                                        }}
                                    >
                                        ESTIMATED COST SAVINGS
                                    </span>
                                </div>
                                <div
                                    style={{
                                        width: "20%",
                                        margin: "10px",
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: "14px",
                                        }}
                                    >
                                        ${operatingCostSaving?.toFixed(2) || 0.0}/y
                                    </span>
                                </div>
                            </div>
                        )}
                        {displayCarbonInfo && (
                            <>
                                <div
                                    style={{
                                        borderBottom: "2px solid #C5C5C6",
                                        width: "100%",
                                        display: "flex",
                                        flex: 1,
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "90%",
                                            paddingLeft: "10px",
                                            borderRight: "2px solid #C5C5C6",
                                            alignContent: "center",
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: "16px",
                                                fontWeight: "800",
                                                color: "#626261",
                                            }}
                                        >
                                            ESTIMATED UPFRONT (EMBODIED) CARBON FROM RETROFIT
                                        </span>
                                    </div>
                                    <div
                                        style={{
                                            width: "20%",
                                            margin: "10px",
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: "14px",
                                            }}
                                        >
                                            {estimatedUpfront?.toFixed(2) || 0.0} Tonnes
                                        </span>
                                    </div>
                                </div>
                                <div
                                    style={{
                                        width: "100%",
                                        display: "flex",
                                        flex: 1,
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "90%",
                                            paddingLeft: "10px",
                                            borderRight: "2px solid #C5C5C6",
                                            alignContent: "center",
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: "16px",
                                                fontWeight: "800",
                                                color: "#626261",
                                            }}
                                        >
                                            CARBON PAYBACK* (EMBODIED INCREASE/OPERATIONAL REDUCTION)
                                        </span>
                                    </div>
                                    <div
                                        style={{
                                            width: "20%",
                                            margin: "10px",
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: "14px",
                                            }}
                                        >
                                            {carbonPayback?.toFixed(2) || 0.0} Years
                                        </span>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            {!displayCarbonInfo && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "end",
                        overflow: "hidden",
                    }}
                >
                    <img src={FooterImage} alt="footer" />
                </div>
            )}
        </div>
    );
};

export default NZReportPDFPage3;
